import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ErrorState,
  LoadingState,
} from '@buakei/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ShieldOff } from 'lucide-react';
import { api } from '@/services/api';
import { Page } from '@/shell/Page';

/** Operations console (OPS-003): non-sensitive metadata only; no client matter content. */
export function OperationsPage() {
  const health = useQuery({ queryKey: ['health'], queryFn: api.health, retry: false });
  return (
    <Page
      title="Operations"
      description="Platform health, accounts, billing and support."
      width="wide"
    >
      <p className="flex max-w-3xl items-start gap-2 text-sm text-ink-900">
        <ShieldOff className="mt-0.5 size-4 shrink-0" aria-hidden />
        Operations access excludes client matters, confidential documents and private research. Any
        exceptional access will require a specific, audited grant.
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle as="h2">Service health</CardTitle>
            <CardDescription>Live check against the Buakei AI API.</CardDescription>
          </CardHeader>
          <CardContent>
            {health.isPending ? <LoadingState label="Checking service health" /> : null}
            {health.isError ? (
              <ErrorState title="The API did not respond" onRetry={() => void health.refetch()}>
                {health.error.message}
              </ErrorState>
            ) : null}
            {health.data ? (
              <p className="flex items-center gap-2 text-sm text-ink-900">
                <Badge tone="success">Operational</Badge> Environment: {health.data.environment}
              </p>
            ) : null}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle as="h2">Billing</CardTitle>
            <CardDescription>Subscriptions and AI credits.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-ink-900">
            <p className="font-medium text-navy-900">Not available</p>
            <p className="mt-2">
              No payment provider is connected. No payment can be taken or confirmed in this build.
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle as="h2">Accounts</CardTitle>
            <CardDescription>Real accounts sign in with Firebase Authentication.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-ink-900">
            Platform administrators grant workspace access under Access. DEMO personas are
            fictitious and separate from real accounts.
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle as="h2">Support</CardTitle>
            <CardDescription>Support tickets exclude private content by default.</CardDescription>
          </CardHeader>
          <CardContent className="text-sm text-ink-900">
            Readers write to the team with Feedback (top of every page). Platform administrators
            read and close them under{' '}
            <Link to="/operations/support" className="font-medium text-navy-900 underline">
              Support
            </Link>
            .
          </CardContent>
        </Card>
      </div>
    </Page>
  );
}
